import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { toast, ToastContainer } from 'react-toastify';
import { useUser } from '../context/UserContext';

const normalizeEmailValue = (value) =>
  String(value || '').trim().toLowerCase();

const getEmailChangeContinueUrl = () => {
  if (
    typeof window !== 'undefined' &&
    /^https?:\/\//.test(window.location.origin || '')
  ) {
    return `${window.location.origin}/confirm-new-email`;
  }
  return 'https://www.blue-wire.org/confirm-new-email';
};

export default function VerifyNewEmailChange() {
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendPassword, setResendPassword] = useState('');
  const [requireResendPassword, setRequireResendPassword] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const pendingEmailChange = currentUser?.pendingEmailChange || null;
  const hasPendingEmailChange = pendingEmailChange?.status === 'pending';
  const pendingEmail = pendingEmailChange?.newEmail || '';
  const currentAuthEmail = normalizeEmailValue(getAuth().currentUser?.email);
  const pendingMatchesAuth =
    hasPendingEmailChange &&
    normalizeEmailValue(pendingEmail) === currentAuthEmail;

  const resendButtonLabel = requireResendPassword
    ? 'Verify password and resend'
    : 'Resend confirmation email';

  const infoText = useMemo(() => {
    if (!pendingEmail) {
      return 'No pending email change was found for this account.';
    }

    return `We sent a confirmation link to ${pendingEmail}. Your current email remains active until you confirm the new one. Once confirmed, your account will require ID verification again.`;
  }, [pendingEmail]);

  useEffect(() => {
    if (!loading && !pendingEmailChange) {
      navigate('/my-account', { replace: true });
    }
  }, [loading, navigate, pendingEmailChange]);

  useEffect(() => {
    if (
      currentUser?.pendingEmailChange?.status === 'completed' &&
      currentUser?.verificationStatus !== 'approved'
    ) {
      navigate('/verify-id', { replace: true });
    }
  }, [
    currentUser?.pendingEmailChange?.status,
    currentUser?.verificationStatus,
    navigate,
  ]);

  useEffect(() => {
    if (!hasPendingEmailChange) return undefined;

    const auth = getAuth();
    const reloadIfPending = async () => {
      if (!auth.currentUser) return;
      try {
        await auth.currentUser.reload();
      } catch (err) {
        console.warn('Failed to refresh email-change auth state:', err);
      }
    };

    reloadIfPending();

    const handleFocus = () => {
      reloadIfPending();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reloadIfPending();
      }
    };

    const intervalId = window.setInterval(() => {
      reloadIfPending();
    }, 20000);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasPendingEmailChange]);

  const handleConfirmed = async () => {
    const auth = getAuth();
    if (!auth.currentUser || !hasPendingEmailChange) {
      toast.error('No pending email change was found.');
      return;
    }

    setChecking(true);
    setStatusMessage('');

    try {
      await auth.currentUser.reload();
      const refreshedEmail = normalizeEmailValue(auth.currentUser.email);

      if (refreshedEmail === normalizeEmailValue(pendingEmail)) {
        setStatusMessage('Email confirmed. Finishing your account update now...');
        return;
      }

      setStatusMessage(
        'We still do not see the new email confirmed. Click the link in your inbox, then try again.'
      );
      toast.info('Email not confirmed yet. Check your new inbox and spam folder.');
    } catch (err) {
      console.error('Email confirmation check failed:', err);
      toast.error(err?.message || 'Could not check your email confirmation yet.');
    } finally {
      setChecking(false);
    }
  };

  const resendConfirmation = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !hasPendingEmailChange) {
      toast.error('No pending email change was found.');
      return;
    }

    setResending(true);
    setStatusMessage('');

    try {
      if (requireResendPassword) {
        if (!resendPassword.trim()) {
          toast.error('Enter your current password to resend the confirmation email.');
          return;
        }

        const credential = EmailAuthProvider.credential(
          user.email || '',
          resendPassword
        );
        await reauthenticateWithCredential(user, credential);
      }

      await verifyBeforeUpdateEmail(user, pendingEmail, {
        url: getEmailChangeContinueUrl(),
        handleCodeInApp: true,
      });

      setRequireResendPassword(false);
      setResendPassword('');
      setStatusMessage(`A new confirmation email was sent to ${pendingEmail}.`);
      toast.success(`Confirmation email sent to ${pendingEmail}.`);
    } catch (err) {
      if (
        err?.code === 'auth/wrong-password' ||
        err?.code === 'auth/invalid-credential'
      ) {
        toast.error('Incorrect password.');
        return;
      }

      if (err?.code === 'auth/requires-recent-login') {
        setRequireResendPassword(true);
        toast.info('Please confirm your password to resend the email.');
        return;
      }

      if (err?.code === 'auth/email-already-in-use') {
        toast.error('That email address is already in use.');
        return;
      }

      if (err?.code === 'auth/invalid-email') {
        toast.error('The pending email address is not valid.');
        return;
      }

      console.error('Failed to resend email-change confirmation:', err);
      toast.error(err?.message || 'Could not resend the confirmation email.');
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">Loading...</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
        <h1 className="text-xl font-semibold text-slate-900">Confirm Your New Email</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">{infoText}</p>

        {pendingEmail && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Pending new email: <strong>{pendingEmail}</strong>
          </div>
        )}

        {statusMessage && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {statusMessage}
          </div>
        )}

        {pendingMatchesAuth && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            The new email is confirmed. BlueWire is finalizing your account update now.
          </div>
        )}

        {requireResendPassword && (
          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Current password
            </label>
            <input
              type="password"
              value={resendPassword}
              onChange={(event) => setResendPassword(event.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              placeholder="Enter current password"
            />
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            onClick={handleConfirmed}
            disabled={checking || !hasPendingEmailChange}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            {checking ? 'Checking confirmation...' : "I've confirmed my new email"}
          </button>
          <button
            type="button"
            onClick={resendConfirmation}
            disabled={resending || !hasPendingEmailChange}
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {resending ? 'Sending...' : resendButtonLabel}
          </button>
          <button
            type="button"
            onClick={() => navigate('/my-account')}
            className="w-full rounded-lg border border-transparent px-4 py-3 text-sm font-medium text-slate-500 transition hover:bg-slate-100"
          >
            I'll do this later
          </button>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}
